import type { SidebarView } from "@/components/board-sidebar";

/**
 * How the rest of the app asks the desktop workspace for a window without knowing
 * it exists: a view carried by an HTML5 drag (a board row out of the navigation,
 * an agent row out of the Agents list), and "open beside" read from the modifier
 * held on the click that led to a `go` — so a view's links stay plain callbacks
 * and none of them has to learn about windows.
 */

export const VIEW_DRAG_TYPE = "application/x-board-view";

export function setViewDrag(data: DataTransfer, view: SidebarView) {
  data.setData(VIEW_DRAG_TYPE, JSON.stringify(view));
  data.effectAllowed = "copyMove";
}

/** Whether a drag carries a view; the payload itself is readable only on drop. */
export const hasViewDrag = (data: DataTransfer | null): boolean => Boolean(data?.types.includes(VIEW_DRAG_TYPE));

export function readViewDrag(data: DataTransfer | null): SidebarView | null {
  const raw = data?.getData(VIEW_DRAG_TYPE);
  if (!raw) return null;
  try {
    const view = JSON.parse(raw) as SidebarView;
    return typeof view === "object" && view && typeof view.kind === "string" ? view : null;
  } catch {
    return null;
  }
}

/** A click this recent with Ctrl or Cmd held means the link it triggers opens beside. */
const BESIDE_WINDOW_MS = 800;
let besideAt = 0;
let tracking = false;

/** Starts noting Ctrl/Cmd-clicks (and middle clicks), once, in the capture phase. */
export function trackOpenBeside() {
  if (tracking || typeof window === "undefined") return;
  tracking = true;
  const note = (event: MouseEvent) => {
    besideAt = event.metaKey || event.ctrlKey || event.button === 1 ? Date.now() : 0;
  };
  window.addEventListener("click", note, true);
  window.addEventListener("auxclick", note, true);
}

/** Whether the click that led here asked to open beside; asking consumes it. */
export function takeOpenBeside(): boolean {
  const beside = besideAt > 0 && Date.now() - besideAt < BESIDE_WINDOW_MS;
  besideAt = 0;
  return beside;
}

/** Asks for the next `go` to open beside, as a Ctrl/Cmd-click would: a menu's "Open in new window". */
export function requestOpenBeside() {
  besideAt = Date.now();
}

/** A key that went to a text field: Option+L or Option+N types a character there on a Mac. */
export function isEditableTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  return Boolean(element?.isContentEditable || element?.tagName === "INPUT" || element?.tagName === "TEXTAREA" || element?.tagName === "SELECT");
}
